import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Buildings, Check, X, Plus, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = ["Police", "Fire", "Hospital", "Healthcare", "EMS / Paramedic", "Security", "Corrections", "Education", "Government", "Transportation", "Aviation", "Telecommunications", "Utilities", "Manufacturing", "Retail", "Banking", "Insurance", "Technology", "Construction", "Hospitality", "Logistics", "Military", "Non-Profit", "Other"];

export default function AdminOrganizations() {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", type: "Other", country: "", state: "", city: "", website: "", email_domain: "", facilities: "", units: "" });

  const { data: pending = [] } = useQuery({ queryKey: ["admin-orgs", "pending"], queryFn: async () => (await api.get("/admin/organizations?status=pending")).data });
  const { data: all = [] } = useQuery({ queryKey: ["admin-orgs", "all"], queryFn: async () => (await api.get("/admin/organizations")).data });

  const act = useMutation({
    mutationFn: async ({ id, action }) => api.post(`/admin/organizations/${id}/${action}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-orgs"] }); toast.success("Done."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const create = useMutation({
    mutationFn: async () => (await api.post("/admin/organizations", {
      name: f.name, type: f.type, country: f.country, state: f.state, city: f.city, website: f.website, email_domain: f.email_domain,
      facilities: f.facilities.split(",").map((s) => s.trim()).filter(Boolean),
      units: f.units.split(",").map((s) => s.trim()).filter(Boolean),
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-orgs"] }); toast.success("Organization created & verified."); setF({ name: "", type: "Other", country: "", state: "", city: "", website: "", email_domain: "", facilities: "", units: "" }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-organizations-page">
      <div className="space-y-6">
        {/* Approval queue */}
        <Card className="p-5 bg-card border-border">
          <h3 className="font-black mb-3">Approval queue {pending.length > 0 && <Badge className="bg-amber-500/15 text-amber-300 border-0 ml-1">{pending.length}</Badge>}</h3>
          <div className="space-y-2" data-testid="org-approval-queue">
            {pending.length === 0 && <p className="text-sm text-muted-foreground">No pending organization requests.</p>}
            {pending.map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2.5" data-testid={`pending-org-${o.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{o.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{o.type}{o.state ? ` · ${o.state}` : ""}{o.country ? ` · ${o.country}` : ""}</p>
                  {o.website && <a href={o.website.startsWith("http") ? o.website : `https://${o.website}`} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline truncate block">{o.website}</a>}
                  {o.requested_by_email && <p className="text-[11px] text-muted-foreground truncate">by {o.requested_by_email}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => act.mutate({ id: o.id, action: "approve" })} data-testid={`approve-org-${o.id}`} className="rounded-md"><Check className="h-4 w-4" weight="bold" /></Button>
                  <Button size="sm" variant="outline" onClick={() => act.mutate({ id: o.id, action: "reject" })} data-testid={`reject-org-${o.id}`} className="rounded-md"><X className="h-4 w-4" weight="bold" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Create org */}
        <Card className="p-5 bg-card border-border">
          <div className="flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-primary" weight="bold" /><h3 className="font-black">Add organization</h3></div>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="new-org-name" className="mt-1.5 bg-secondary/50" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div>
              <Label>Industry type</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger data-testid="new-org-type" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Website</Label><Input data-testid="new-org-website" className="mt-1.5 bg-secondary/50" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="https://example.org" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Country</Label><Input data-testid="new-org-country" className="mt-1.5 bg-secondary/50" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
              <div><Label>State</Label><Input data-testid="new-org-state" className="mt-1.5 bg-secondary/50" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} /></div>
              <div><Label>City</Label><Input data-testid="new-org-city" className="mt-1.5 bg-secondary/50" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
            </div>
            <div><Label>Work email domain(s)</Label><Input data-testid="new-org-domain" className="mt-1.5 bg-secondary/50" value={f.email_domain} onChange={(e) => setF({ ...f, email_domain: e.target.value })} placeholder="e.g. nhs.uk, example.org" /></div>
            <div><Label>Facilities (comma-separated)</Label><Input data-testid="new-org-facilities" className="mt-1.5 bg-secondary/50" value={f.facilities} onChange={(e) => setF({ ...f, facilities: e.target.value })} /></div>
            <div><Label>Divisions / Departments (comma-separated)</Label><Input data-testid="new-org-units" className="mt-1.5 bg-secondary/50" value={f.units} onChange={(e) => setF({ ...f, units: e.target.value })} /></div>
            <Button onClick={() => create.mutate()} disabled={!f.name || create.isPending} data-testid="new-org-submit" className="rounded-md font-semibold">Create organization</Button>
          </div>
        </Card>
      </div>

      {/* All orgs */}
      <Card className="p-5 bg-card border-border h-fit">
        <h3 className="font-black mb-3">All organizations ({all.length})</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="all-orgs-list">
          {all.map((o) => (
            <div key={o.id} className="flex items-center justify-between rounded-md bg-secondary/30 px-3 py-2" data-testid={`org-row-${o.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Buildings className="h-4 w-4 text-muted-foreground shrink-0" weight="bold" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate flex items-center gap-1">{o.name}{o.verified && <SealCheck className="h-3.5 w-3.5 text-sky-400 shrink-0" weight="fill" data-testid={`org-verified-${o.id}`} />}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{o.type} · {o.units?.length || 0} units{o.verification_method ? ` · ${o.verification_method.replace(/_/g, " ")}` : ""}</p>
                </div>
              </div>
              <Badge className={`border-0 text-[10px] uppercase shrink-0 ${o.status === "approved" ? "bg-emerald-500/15 text-emerald-400" : o.status === "pending" ? "bg-amber-500/15 text-amber-300" : "bg-secondary text-muted-foreground"}`}>{o.status}</Badge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
